import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { auth } from '../../configs/firebase';
import { adminConfig } from '../config/AdminConfig';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if admin mode is enabled
  const isAdminMode = adminConfig.isAdminMode();

  // Check if user is admin
  const checkAdminStatus = async (firebaseUser) => {
    if (!firebaseUser?.email || !isAdminMode) {
      setIsAdmin(false);
      return false;
    }

    try {
      const adminStatus = await adminConfig.isAdminEmail(firebaseUser.email);
      setIsAdmin(adminStatus);
      return adminStatus;
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      return false;
    }
  };

  // Admin login
  const adminLogin = async (email, password) => {
    if (!isAdminMode) {
      throw new Error('Admin mode is not enabled');
    }

    try {
      setLoading(true);
      
      // First check if email is in admin list
      const isEmailAdmin = await adminConfig.isAdminEmail(email);
      if (!isEmailAdmin) {
        throw new Error('Access denied. Not an authorized admin email.');
      }

      // Attempt Firebase login
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Double-check admin status after login
      const adminStatus = await checkAdminStatus(firebaseUser);
      if (!adminStatus) {
        await signOut(auth);
        throw new Error('Access denied. Admin privileges not found.');
      }

      console.log('Admin login successful:', firebaseUser.email);
      return firebaseUser;
      
    } catch (error) {
      console.error('Admin login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Admin logout
  const adminLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setIsAdmin(false);
    } catch (error) {
      console.error('Admin logout error:', error);
      throw error;
    }
  };

  // Add new admin email
  const addAdminEmail = async (email) => {
    return await adminConfig.addAdminEmail(email);
  };

  // Remove admin email
  const removeAdminEmail = async (email) => {
    return await adminConfig.removeAdminEmail(email);
  };

  // Listen to auth state changes
  useEffect(() => {
    if (!isAdminMode) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      
      if (firebaseUser) {
        const adminStatus = await checkAdminStatus(firebaseUser);
        
        if (adminStatus) {
          setUser(firebaseUser);
          setIsAdmin(true);
        } else {
          await signOut(auth);
          setUser(null);
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, [isAdminMode]);

  const value = {
    user,
    isAdmin,
    loading,
    isAdminMode,
    
    // Authentication methods
    adminLogin,
    adminLogout,
    
    // Admin management methods
    addAdminEmail,
    removeAdminEmail,
    
    // Utility methods
    checkAdminStatus: () => isAdmin,
    isAuthenticated: () => !!user && isAdmin,
    getUserEmail: () => user?.email,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};